// @ts-strict
import { Injectable, ViewContainerRef } from '@angular/core';
import { LMContextService } from './lm-context.service';
import { LMBusyIndicatorService } from './lm-busyindicator.service';
import { LMSpecService } from './lm-spec.service';
import { LMApplicationMenuSubMenuService } from '@components/applicationmenu/submenu/lm-application-menu-submenu.service';
import { LMErrorUtilService } from './utils/lm-error-util.service';
import { LMFormatUtilService } from './utils/lm-format-util.service';
import { LMUrlUtilService } from './utils/lm-url-util.service';
import { LMBrowserUtilService } from '../utils/lm-browser-util.service';
import { LMContextUtilService } from '../utils/lm-context-util.service';
import { LMGlobalUtilService } from '../utils/lm-global-util.service';
import { LMLoggerService } from './lm-logger.service';
import {LMWebAppService} from "@services/lm-webapp.service";

@Injectable()
export class LMMenuLinkBoxService {
  constructor(
      private lmBusyIndicatorService: LMBusyIndicatorService,
      private lmSpecService: LMSpecService,
      private lmApplicationMenuSubMenuService: LMApplicationMenuSubMenuService,
      private lmUrlUtilService: LMUrlUtilService,
      private lmContextService: LMContextService,
      private lmContextUtilService: LMContextUtilService,
      private lmBrowserUtilService: LMBrowserUtilService,
      private lmLoggerService: LMLoggerService,
      private lmGlobalUtilService: LMGlobalUtilService,
      private lmErrorUtilService: LMErrorUtilService,
      private lmFormatUtilService: LMFormatUtilService,
      private lmWebAppService: LMWebAppService
  ) { }

  public runMenuLinkBoxAction(link: LinkInfo, placeholder: ViewContainerRef): void {
    if (link?.menu.link) {
      this.lmBusyIndicatorService.showBusyIndicator(); // Dialog open will hide the busy indicator
      this.lmSpecService.getApplicationMenuItemSpec(link.menu.link).subscribe({
        next: (response) => {
          if (response.resourceType === 'action') {
            this.lmApplicationMenuSubMenuService.runAction(response.dialogUrl || response.redirectUrl, response.dataView, placeholder);
          } else if (response.dialogUrl) {
            this.lmApplicationMenuSubMenuService.requestActionFromPageMenu(response.dialogUrl, response.resourceId, placeholder);
          } else if (response.redirectUrl) {
            this.lmContextService.setClearNextPageState(true);
            const url = response.redirectUrl;
            const noDefaultPortUrl = this.lmUrlUtilService.stripDefaultPort(url);
            let host = this.lmContextUtilService.getLocation(noDefaultPortUrl).host;
            // In IE, the host value includes the port number, we need to strip it so it will match the window.location.host
            if (this.lmBrowserUtilService.isIE) {
              host = this.lmUrlUtilService.stripDefaultPortFromHost(host);
            }
            const log = {
              codeLoc: 'lm-application-menu.component.td - onMenuItemClicked',
              url,
              noDefaultPortUrl,
              host,
              'window.location.host': window.location.host,
              'url.length': url.length,
              doHostsMatch: (host.toLowerCase() === window.location.host.toLowerCase()),
            };
            this.lmLoggerService.log(log);

            if (response.external) {
              this.lmBusyIndicatorService.hideBusyIndicator();
              this.lmUrlUtilService.openExternalUrl(response.redirectUrl);
            } else if (response.embedded) {
              if ((url.length > 0 && host !== window.location.host) || (response.redirectUrl.indexOf('.html') >= 0)) {
                const webapp = this.lmContextService.getContext().webApp;
                const dataArea = this.lmContextService.getContext().urlPdlOrDa;
                let autoLoadUrl = dataArea + '/' + webapp + '/html/url?autoLoadUrl=' + encodeURIComponent(response.redirectUrl);
                autoLoadUrl = this.lmUrlUtilService.updateContextSessionKeyParams(autoLoadUrl);
                this.lmGlobalUtilService.navigateByUrl(autoLoadUrl);
              } else {
                this.lmGlobalUtilService.navigateByUrl(noDefaultPortUrl);
              }
            } else {
              if ((url.length > 0 && host !== window.location.host) || (response.redirectUrl.indexOf('.html') >= 0)) {
                window.location.href = response.redirectUrl;
              } else {
                this.lmGlobalUtilService.navigateByUrl(noDefaultPortUrl);
              }
            }
          } else {
            this._handleError(response, placeholder);
          }
        },
        error: (e) => {
          this._handleError(e, placeholder);
        }
      });
    }
  }

  public buildLinkMenuSpec(menuPanelLayout: LMMenuPanelLayout, dataView: LMDataView): Array<LinkInfo> {
    const links: Array<LinkInfo> = [];
    if (menuPanelLayout?.sections) {
      for (const section of menuPanelLayout.sections) {
        if (section.menu && section.width) {
          const menu: LMFormLayoutMenu = section.menu;
          let title = '';
          let description = '';
          let visible = true;
          if (menu?.label) {
            title = this.lmFormatUtilService.formatMessageRule(menu.label, dataView);
          }
          if (menu?.description) {
            description = this.lmFormatUtilService.formatMessageRule(menu.description, dataView);
          }
          if (this.lmWebAppService.isPageMenuItemInaccessible(section.menu)) {
            visible = false;
          }
          links.push({ menu: menu, title: title, width: section.width, description, visible });
        }
      }
    }
    this.updateWidths(links);
    return links;
  }

  private _handleError(error: any, placeholder?: ViewContainerRef): void {
    this.lmErrorUtilService.handleUnexpectedError(error, placeholder);
  }

  private updateWidths(links: Array<LinkInfo>): void {
    if (!links.some((link: LinkInfo) => !link.visible)) {
      // no hidden menu items, noop
      return;
    }
    const numLinks = links.length;
    switch(numLinks){
      case 2:
        // 2 dual lengths, can't gain back any space. Noop
        return;
      case 3:
        // 1 dual length and 2 single lengths. If dual length not visible, make both singles into duals. If single length
        // not visible, then make other single dual length. If dual and a single not visible, update remaining visible
        // single to dual length. Parsing restricts single dual single in the LPL, so either index 0 or index 2 will be
        // the dual length
        links[0].width = 'double';
        links[1].width = 'double';
        links[2].width = 'double';
        return;
      case 4:
        if (!links[0].visible) {
          if (links[1].visible) {
            links[1].width = 'double';
            if (links[2].visible) {
              if (links[3].visible) {
                // 1, 2 and 3 are all visible, and 1 has been made double. Keep the other two single, and break
                return;
              } else {
                // only 1 and 2 are visible, set 2 to double width
                links[2].width = 'double';
              }
            } else {
              // 0 and 2 are hidden, set 1 and 3 to double width
              links[3].width = 'double';
            }
          } else {
            // link index 0 and link index 1 both not visible, just set the next two to double
            links[2].width = 'double';
            links[3].width = 'double';
          }
        } else {
          // 0 is visible
          if (links[1].visible) {
            // 0 and 1 are visible
            if (links[2].visible) {
              if (!links[3].visible) {
                //2 is visible, 3 is not. Make 2 double
                links[2].width = 'double';
              } else {
                // all 4 are visible, this shouldn't be hit. Just break
                break;
              }
            } else {
              //2 is not visible
              if (links[3].visible) {
                // 3 is visible, make it double length
                links[3].width = 'double';
              } else {
                // 2 and 3 are not visible, make 0 and 1 double
                links[0].width = 'double';
                links[1].width = 'double';
              }
            }
          } else {
            // 1 is not visible
            links[0].width = 'double';
            if (links[2].visible) {
              // 0 and 2 are visible
              if (links[3].visible) {
                // 0, 2, 3 are visible, 0 has been made double already. Just return
                return;
              } else {
                // 0 and 2 are visible, 1 and 3 are not visible. Set 2 to double (0 has been above)
                links[2].width = 'double';
              }
            } else {
              // 1 and 2 are not visible. 0 and 3 could be visible. Set 3 to double (0 has been above)
              links[3].width = 'double';
            }
          }
        }
        return;
    }
  }
}
